import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { landingPath, toLandingLang, type LandingId, type LandingLang } from '@/lib/marketing/localizedLandings';

const LANG_KEY = 'bookinghub_language';

/**
 * Keeps a landing page's URL and language in step (see localizedLandings.ts):
 * - a localized URL (/de/fuer-friseursalons) switches the page to its language;
 * - the English URL (/for-salons) sends a visitor who reads another language to that
 *   language's URL, so every language is served from exactly one address;
 * - switching language on the page moves to the URL of the new language.
 */
export function LocalizedLanding({ id, lang, children }: { id: LandingId; lang: LandingLang; children: ReactNode }) {
  const { i18n } = useTranslation();
  const [location, setLocation] = useLocation();

  // English URL, other reader language: where they should be instead (null = stay).
  const redirectTo = (language: string) => {
    const target = landingPath(id, language);
    return target !== location ? target : null;
  };

  const [ready, setReady] = useState(() =>
    lang === 'en' ? redirectTo(i18n.language) === null : toLandingLang(i18n.language) === lang,
  );

  useEffect(() => {
    if (lang === 'en') {
      const target = redirectTo(i18n.language);
      if (target) {
        setLocation(target, { replace: true });
        return;
      }
    } else if (toLandingLang(i18n.language) !== lang) {
      i18n.changeLanguage(lang);
      try {
        window.localStorage.setItem(LANG_KEY, lang);
      } catch {
        // Private mode: the URL still decides the language.
      }
    }
    setReady(true);
    // Run once per landing URL; later switches are handled by the listener below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lang]);

  useEffect(() => {
    const onChange = (language: string) => {
      const target = landingPath(id, language);
      if (target !== window.location.pathname) setLocation(target);
    };
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, [i18n, id, setLocation]);

  return ready ? <>{children}</> : null;
}
